import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, ArrowLeft, CheckCircle, User, Clock, Heart, Phone, AlertTriangle } from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnrollChild = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Basic, 2: Routine, 3: Context, 4: Contact
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    dateOfBirth: '',
    gender: '',
    grade: '',
    school: '',
    
    // Contact for calls
    childPhone: '',
    alternatePhone: '',
    
    // Routine
    wakeTime: '',
    schoolStartTime: '',
    schoolEndTime: '',
    bedtime: '',
    
    // Preferred Call Slots
    preferredSlots: [],
    preferredDays: [],
    
    // Context - Concerns
    primaryConcerns: [],
    concernDetails: '',
    
    // Context - Strengths
    strengths: [],
    interests: [],
    
    // Communication
    preferredLanguage: 'English',
    communicationStyle: '',
    
    // Medical (Optional)
    medicalNotes: '',
    allergies: '',
    specialNeeds: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    
    // Additional
    previousCounseling: false,
    additionalNotes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('enroll_token');
    if (!token) {
      navigate('/enroll/register');
    }
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('enroll_token')}` }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const toggleArrayItem = (field, item) => {
    const current = formData[field];
    if (current.includes(item)) {
      setFormData({ ...formData, [field]: current.filter(i => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...current, item] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/enroll/child`,
        null,
        {
          params: {
            name: formData.name,
            date_of_birth: formData.dateOfBirth,
            gender: formData.gender,
            grade: formData.grade,
            school: formData.school,
            child_phone: formData.childPhone,
            alternate_phone: formData.alternatePhone,
            wake_time: formData.wakeTime,
            school_start_time: formData.schoolStartTime,
            school_end_time: formData.schoolEndTime,
            bedtime: formData.bedtime,
            preferred_call_slots: JSON.stringify(formData.preferredSlots),
            preferred_days: JSON.stringify(formData.preferredDays),
            primary_concerns: JSON.stringify(formData.primaryConcerns),
            concern_details: formData.concernDetails,
            strengths: JSON.stringify(formData.strengths),
            interests: JSON.stringify(formData.interests),
            preferred_language: formData.preferredLanguage,
            communication_style: formData.communicationStyle,
            medical_notes: formData.medicalNotes,
            allergies: formData.allergies,
            special_needs: formData.specialNeeds,
            emergency_contact_name: formData.emergencyContactName,
            emergency_contact_phone: formData.emergencyContactPhone,
            emergency_contact_relation: formData.emergencyContactRelation,
            previous_counseling: formData.previousCounseling,
            additional_notes: formData.additionalNotes
          },
          ...getAuthHeaders()
        }
      );
      
      navigate('/enroll/payment');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enroll child. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const concernOptions = [
    'Anxiety', 'Low Confidence', 'Social Difficulties', 'Academic Stress',
    'Emotional Regulation', 'Anger Issues', 'Peer Relationships', 'Family Changes',
    'Sleep Issues', 'Attention/Focus', 'Bullying', 'Other'
  ];

  const strengthOptions = [
    'Creative', 'Empathetic', 'Curious', 'Athletic', 'Musical',
    'Good with numbers', 'Strong reader', 'Artistic', 'Problem solver',
    'Good memory', 'Leadership', 'Kind to others'
  ];

  const interestOptions = [
    'Sports', 'Music', 'Art/Drawing', 'Reading', 'Gaming', 'Science',
    'Animals', 'Nature', 'Cooking', 'Dancing', 'Building/LEGO', 'Movies'
  ];

  const timeSlots = [
    '4:00 PM - 5:00 PM', '5:00 PM - 6:00 PM', '6:00 PM - 7:00 PM',
    '7:00 PM - 8:00 PM', '8:00 PM - 9:00 PM'
  ];

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const languages = ['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi', 'Bengali', 'Gujarati'];

  const communicationStyles = [
    { value: 'talkative', label: 'Talkative - Loves to chat' },
    { value: 'reserved', label: 'Reserved - Takes time to open up' },
    { value: 'shy', label: 'Shy - Needs encouragement' },
    { value: 'expressive', label: 'Expressive - Shows emotions openly' },
    { value: 'quiet', label: 'Quiet - Prefers listening' }
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
                <p className="text-sm text-gray-500">Tell us about your child</p>
              </div>
            </div>

            <div>
              <Label>Child&apos;s Full Name *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full name"
                required
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date of Birth *</Label>
                <Input
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Gender *</Label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Grade/Class *</Label>
                <Input
                  name="grade"
                  value={formData.grade}
                  onChange={handleChange}
                  placeholder="e.g., 5th Grade"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>School Name *</Label>
                <Input
                  name="school"
                  value={formData.school}
                  onChange={handleChange}
                  placeholder="School name"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Preferred Language for Sessions</Label>
              <select
                name="preferredLanguage"
                value={formData.preferredLanguage}
                onChange={handleChange}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {languages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Daily Routine & Schedule</h2>
                <p className="text-sm text-gray-500">Help us find the best time for sessions</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Daily Routine (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Wake Time</Label>
                  <Input
                    name="wakeTime"
                    type="time"
                    value={formData.wakeTime}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Bedtime</Label>
                  <Input
                    name="bedtime"
                    type="time"
                    value={formData.bedtime}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>School Start</Label>
                  <Input
                    name="schoolStartTime"
                    type="time"
                    value={formData.schoolStartTime}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>School End</Label>
                  <Input
                    name="schoolEndTime"
                    type="time"
                    value={formData.schoolEndTime}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-base font-medium">Preferred Call Time Slots *</Label>
              <p className="text-sm text-gray-500 mb-2">Select all time slots that work for your child</p>
              <div className="grid grid-cols-2 gap-2">
                {timeSlots.map(slot => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => toggleArrayItem('preferredSlots', slot)}
                    className={`p-3 border rounded-lg text-sm transition-all ${
                      formData.preferredSlots.includes(slot)
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    {formData.preferredSlots.includes(slot) && <CheckCircle className="w-4 h-4 inline mr-1" />}
                    {slot}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-base font-medium">Preferred Days *</Label>
              <p className="text-sm text-gray-500 mb-2">Select days available for sessions</p>
              <div className="flex flex-wrap gap-2">
                {weekDays.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleArrayItem('preferredDays', day)}
                    className={`px-4 py-2 border rounded-full text-sm transition-all ${
                      formData.preferredDays.includes(day)
                        ? 'border-green-500 bg-green-500 text-white'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">About Your Child</h2>
                <p className="text-sm text-gray-500">Help us understand your child better</p>
              </div>
            </div>

            <div>
              <Label className="text-base font-medium">What concerns bring you to Sanjaya?</Label>
              <p className="text-sm text-gray-500 mb-2">Select all that apply</p>
              <div className="flex flex-wrap gap-2">
                {concernOptions.map(concern => (
                  <button
                    key={concern}
                    type="button"
                    onClick={() => toggleArrayItem('primaryConcerns', concern)}
                    className={`px-3 py-2 border rounded-full text-sm transition-all ${
                      formData.primaryConcerns.includes(concern)
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    {concern}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Tell us more about your concerns (Optional)</Label>
              <Textarea
                name="concernDetails"
                value={formData.concernDetails}
                onChange={handleChange}
                placeholder="Share any specific situations, behaviors, or challenges you've noticed..."
                className="mt-1 h-24"
              />
            </div>

            <div>
              <Label className="text-base font-medium">Your Child&apos;s Strengths</Label>
              <p className="text-sm text-gray-500 mb-2">What does your child do well?</p>
              <div className="flex flex-wrap gap-2">
                {strengthOptions.map(strength => (
                  <button
                    key={strength}
                    type="button"
                    onClick={() => toggleArrayItem('strengths', strength)}
                    className={`px-3 py-2 border rounded-full text-sm transition-all ${
                      formData.strengths.includes(strength)
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    {strength}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-base font-medium">Interests & Hobbies</Label>
              <p className="text-sm text-gray-500 mb-2">What does your child enjoy?</p>
              <div className="flex flex-wrap gap-2">
                {interestOptions.map(interest => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleArrayItem('interests', interest)}
                    className={`px-3 py-2 border rounded-full text-sm transition-all ${
                      formData.interests.includes(interest)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Communication Style</Label>
              <select
                name="communicationStyle"
                value={formData.communicationStyle}
                onChange={handleChange}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select how your child typically communicates</option>
                {communicationStyles.map(style => (
                  <option key={style.value} value={style.value}>{style.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                name="previousCounseling"
                checked={formData.previousCounseling}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">My child has received counseling or therapy before</span>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Contact & Safety</h2>
                <p className="text-sm text-gray-500">Important contact information</p>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Phone for Sessions *</h3>
              <p className="text-sm text-gray-600 mb-3">
                Our system will call this number at the scheduled time to connect with your child.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Primary Phone *</Label>
                  <Input
                    name="childPhone"
                    type="tel"
                    value={formData.childPhone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Alternate Phone</Label>
                  <Input
                    name="alternatePhone"
                    type="tel"
                    value={formData.alternatePhone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-medium text-gray-900">Emergency Contact *</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Contact Name *</Label>
                  <Input
                    name="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={handleChange}
                    placeholder="Emergency contact name"
                    required
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      name="emergencyContactPhone"
                      type="tel"
                      value={formData.emergencyContactPhone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Relationship *</Label>
                    <Input
                      name="emergencyContactRelation"
                      value={formData.emergencyContactRelation}
                      onChange={handleChange}
                      placeholder="e.g., Mother, Father"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Medical Information (Optional)</h3>
              <p className="text-sm text-gray-600 mb-3">
                This helps our observers be aware of any special considerations.
              </p>
              <div className="space-y-3">
                <div>
                  <Label>Allergies</Label>
                  <Input
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleChange}
                    placeholder="Any known allergies"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Special Needs or Conditions</Label>
                  <Input
                    name="specialNeeds"
                    value={formData.specialNeeds}
                    onChange={handleChange}
                    placeholder="ADHD, ASD, etc."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Additional Medical Notes</Label>
                  <Textarea
                    name="medicalNotes"
                    value={formData.medicalNotes}
                    onChange={handleChange}
                    placeholder="Any other medical information we should know..."
                    className="mt-1 h-20"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Additional Notes for Our Team</Label>
              <Textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleChange}
                placeholder="Anything else you'd like us to know about your child..."
                className="mt-1 h-20"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.name && formData.dateOfBirth && formData.gender && formData.grade && formData.school;
      case 2:
        return formData.preferredSlots.length > 0 && formData.preferredDays.length > 0;
      case 3:
        return true; // Context is optional
      case 4:
        return formData.childPhone && formData.emergencyContactName && formData.emergencyContactPhone && formData.emergencyContactRelation;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Navigation />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm text-green-600">Package</span>
            </div>
            <div className="w-6 h-0.5 bg-green-500"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm text-green-600">Account</span>
            </div>
            <div className="w-6 h-0.5 bg-green-500"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm text-green-600">Consent</span>
            </div>
            <div className="w-6 h-0.5 bg-orange-500"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">4</div>
              <span className="text-sm font-medium text-orange-600">Child</span>
            </div>
          </div>

          {/* Step Progress */}
          <div className="flex justify-between mb-6">
            {[1, 2, 3, 4].map(s => (
              <div
                key={s}
                className={`flex-1 h-2 mx-1 rounded-full ${
                  s <= step ? 'bg-orange-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={step === 4 ? handleSubmit : (e) => { e.preventDefault(); setStep(step + 1); }}>
                {renderStep()}

                {error && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                    {error}
                  </div>
                )}

                <div className="mt-6 pt-4 border-t flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => step === 1 ? navigate('/enroll/consent') : setStep(step - 1)}
                    className="px-6"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  
                  <Button
                    type="submit"
                    disabled={!canProceed() || (step === 4 && loading)}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-6 disabled:opacity-50"
                  >
                    {step === 4 ? (loading ? 'Saving...' : 'Continue to Payment') : 'Next Step'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EnrollChild;
